<template>
  <div class="container-fluid mt-4" :class="themeClass">

    <!-- Encabezado banco -->
    <div class="d-flex align-items-center gap-2 mb-3">
      <button class="btn btn-outline-secondary btn-sm" @click="$router.back()">
        <i class="bi bi-arrow-left"></i>
      </button>
      <h5 class="mb-0">
        <i class="bi bi-bank me-2"></i>
        {{ banco?.nombre_banco || 'Banco' }}
        <span class="text-muted fw-normal small ms-2">{{ banco?.numero_cuenta }}</span>
      </h5>
    </div>

    <!-- Tarjetas de resumen -->
    <div v-if="banco" class="row g-3 mb-4">
      <div class="col-6 col-md-3">
        <div class="card text-center border-0 shadow-sm resumen-card">
          <div class="card-body py-3">
            <div class="resumen-label small mb-1">Saldo Inicial</div>
            <div class="fs-5 fw-bold resumen-valor">${{ fmt(banco.saldo_inicial) }}</div>
          </div>
        </div>
      </div>
      <div class="col-6 col-md-3">
        <div class="card text-center border-0 shadow-sm resumen-card">
          <div class="card-body py-3">
            <div class="resumen-label small mb-1">Total Ingresos</div>
            <div class="fs-5 fw-bold resumen-ingreso">+${{ fmt(resumen.total_ingresos) }}</div>
          </div>
        </div>
      </div>
      <div class="col-6 col-md-3">
        <div class="card text-center border-0 shadow-sm resumen-card">
          <div class="card-body py-3">
            <div class="resumen-label small mb-1">Total Egresos</div>
            <div class="fs-5 fw-bold resumen-egreso">-${{ fmt(resumen.total_egresos) }}</div>
          </div>
        </div>
      </div>
      <div class="col-6 col-md-3">
        <div class="card text-center border-0 shadow-sm resumen-card resumen-card-saldo">
          <div class="card-body py-3">
            <div class="resumen-label small mb-1">Saldo Actual</div>
            <div class="fs-5 fw-bold resumen-saldo">${{ fmt(saldoActual) }}</div>
          </div>
        </div>
      </div>
    </div>

    <!-- Movimientos -->
    <div class="card shadow-sm">
      <div class="card-header d-flex justify-content-between align-items-center">
        <span class="fw-semibold">Movimientos</span>
        <button v-if="puedeCrear" class="btn btn-sm btn-outline-primary" @click="abrirExterno">
          <i class="bi bi-plus-circle me-1"></i> Movimiento externo
        </button>
      </div>

      <div class="card-body p-0">
        <div v-if="cargando" class="text-center py-4">
          <div class="spinner-border text-primary"></div>
        </div>
        <div v-else-if="error" class="alert alert-danger m-3">{{ error }}</div>
        <div v-else>
          <div class="table-responsive">
            <table class="table table-sm table-hover align-middle mb-0">
              <thead class="table-light">
                <tr>
                  <th>#</th>
                  <th>Fecha</th>
                  <th>Tipo</th>
                  <th>Origen</th>
                  <th>N.° Comp.</th>
                  <th>Descripción</th>
                  <th class="text-end">Monto</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="movimientos.length === 0">
                  <td colspan="8" class="text-center py-4 text-muted">
                    <i class="bi bi-inbox me-2"></i>No hay movimientos
                  </td>
                </tr>
                <tr v-for="m in movimientos" :key="m.id">
                  <td class="text-muted small">{{ m.id }}</td>
                  <td class="text-muted small">{{ formatearFechaHora(m.created_at) }}</td>
                  <td>
                    <span :class="m.tipo === 'ingreso' ? 'badge bg-success' : 'badge bg-danger'">
                      {{ m.tipo }}
                    </span>
                  </td>
                  <td><span class="badge bg-secondary text-capitalize">{{ m.origen }}</span></td>
                  <td class="small">{{ m.numero_comprobante || '—' }}</td>
                  <td class="small">{{ m.descripcion || '—' }}</td>
                  <td class="text-end fw-semibold" :class="m.tipo === 'ingreso' ? 'text-success' : 'text-danger'">
                    {{ m.tipo === 'ingreso' ? '+' : '-' }}${{ fmt(m.monto) }}
                  </td>
                  <td class="text-end" style="white-space:nowrap">
                    <template v-if="esManual(m)">
                      <button v-if="puedeEditar" class="btn btn-outline-secondary btn-sm me-1" title="Editar" @click="abrirEditar(m)">
                        <i class="bi bi-pencil"></i>
                      </button>
                      <button v-if="puedeEliminar" class="btn btn-outline-danger btn-sm" title="Eliminar" @click="eliminar(m)">
                        <i class="bi bi-trash"></i>
                      </button>
                    </template>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Paginación -->
          <div v-if="paginacion.paginas > 1" class="d-flex justify-content-center gap-2 py-3">
            <button
              class="btn btn-outline-secondary btn-sm"
              :disabled="paginacion.pagina <= 1"
              @click="cambiarPagina(paginacion.pagina - 1)"
            >
              <i class="bi bi-chevron-left"></i>
            </button>
            <span class="align-self-center small text-muted">
              Pág. {{ paginacion.pagina }} / {{ paginacion.paginas }}
            </span>
            <button
              class="btn btn-outline-secondary btn-sm"
              :disabled="paginacion.pagina >= paginacion.paginas"
              @click="cambiarPagina(paginacion.pagina + 1)"
            >
              <i class="bi bi-chevron-right"></i>
            </button>
          </div>
        </div>
      </div>
    </div>

  </div>

  <!-- Modal edición de movimiento manual -->
  <div v-if="modalEditar.visible" class="modal d-block" tabindex="-1" style="background:rgba(0,0,0,.5)"><div class="modal-dialog">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title"><i class="bi bi-pencil me-2"></i>Editar movimiento #{{ modalEditar.id }}</h5>
          <button type="button" class="btn-close" @click="modalEditar.visible = false"></button>
        </div>
        <div class="modal-body">
          <div v-if="modalEditar.error" class="alert alert-danger py-2">{{ modalEditar.error }}</div>
          <div class="mb-3">
            <label class="form-label fw-semibold">Monto</label>
            <div class="input-group">
              <span class="input-group-text">$</span>
              <input v-model="modalEditar.monto" type="number" step="0.01" min="0.01" class="form-control" />
            </div>
          </div>
          <div class="mb-3">
            <label class="form-label fw-semibold">Descripción</label>
            <input v-model="modalEditar.descripcion" type="text" class="form-control" maxlength="255" />
          </div>
          <div v-if="modalEditar.esTransferencia" class="alert alert-info py-2 small">
            <i class="bi bi-info-circle me-1"></i> Este depósito también actualizará el movimiento de caja asociado.
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn btn-secondary" @click="modalEditar.visible = false">Cancelar</button>
          <button class="btn btn-primary" :disabled="modalEditar.guardando" @click="guardarEdicion">
            <span v-if="modalEditar.guardando" class="spinner-border spinner-border-sm me-1"></span>
            Guardar
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- Modal: Registrar movimiento externo -->
  <div v-if="modalExterno.visible" class="modal d-block" tabindex="-1" style="background:rgba(0,0,0,.5)">
    <div class="modal-dialog">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">
            <i class="bi bi-plus-circle me-2"></i>Movimiento externo
          </h5>
          <button type="button" class="btn-close" @click="modalExterno.visible = false"></button>
        </div>
        <div class="modal-body">
          <div v-if="modalExterno.error" class="alert alert-danger py-2 small">{{ modalExterno.error }}</div>
          <p class="text-muted small mb-3">
            Registra una entrada o salida de dinero en el banco sin afectar facturas internas del sistema.
          </p>
          <!-- Tipo -->
          <div class="mb-3">
            <label class="form-label fw-semibold">Tipo <span class="text-danger">*</span></label>
            <div class="d-flex gap-2">
              <button
                type="button"
                :class="['btn btn-sm flex-fill', modalExterno.tipo === 'ingreso' ? 'btn-success' : 'btn-outline-secondary']"
                @click="modalExterno.tipo = 'ingreso'"
              >
                <i class="bi bi-arrow-down-circle me-1"></i> Ingreso
              </button>
              <button
                type="button"
                :class="['btn btn-sm flex-fill', modalExterno.tipo === 'egreso' ? 'btn-danger' : 'btn-outline-secondary']"
                @click="modalExterno.tipo = 'egreso'"
              >
                <i class="bi bi-arrow-up-circle me-1"></i> Egreso
              </button>
            </div>
          </div>
          <!-- Monto -->
          <div class="mb-3">
            <label class="form-label fw-semibold">Monto <span class="text-danger">*</span></label>
            <div class="input-group">
              <span class="input-group-text">$</span>
              <input
                v-model="modalExterno.monto"
                type="number" step="0.01" min="0.01"
                class="form-control"
              />
            </div>
          </div>
          <!-- Descripción -->
          <div class="mb-3">
            <label class="form-label fw-semibold">Descripción <span class="text-danger">*</span></label>
            <input
              v-model="modalExterno.descripcion"
              type="text"
              class="form-control"
              maxlength="255"
              placeholder="Ej: Pago factura pendiente, cargo bancario..."
            />
          </div>
          <!-- N.° comprobante -->
          <div class="mb-3">
            <label class="form-label fw-semibold">N.° comprobante <span class="text-muted">(opcional)</span></label>
            <input
              v-model="modalExterno.numero_comprobante"
              type="text"
              class="form-control"
              maxlength="100"
              placeholder="Referencia o número de transacción"
            />
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn btn-secondary" @click="modalExterno.visible = false">Cancelar</button>
          <button class="btn btn-primary" :disabled="modalExterno.guardando" @click="guardarExterno">
            <span v-if="modalExterno.guardando" class="spinner-border spinner-border-sm me-1"></span>
            Registrar
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import api from '@/services/api'
import themeManager from '@/store/theme'
import { formatearFechaHoraSistema } from '@/utils/fechaSistema'

export default {
  name: 'ClientBancosMovimientos',

  data() {
    return {
      cargando:    false,
      error:       null,
      banco:       null,
      saldoActual: 0,
      resumen:     { total_ingresos: 0, total_egresos: 0 },
      movimientos: [],
      paginacion:  { total: 0, pagina: 1, por_pagina: 50, paginas: 1 },
      modalEditar: {
        visible: false, guardando: false, error: null,
        id: null, monto: 0, descripcion: '', esTransferencia: false,
      },
      modalExterno: {
        visible: false, guardando: false, error: null,
        tipo: 'egreso', monto: 0, descripcion: '', numero_comprobante: '',
      },
    }
  },

  computed: {
    themeClass() { return themeManager.themeClass },
    puedeCrear() {
      return this.$store.getters.puedoHacer('/finanzas/bancos', 'crear')
    },
    puedeEditar() {
      return this.$store.getters.puedoHacer('/finanzas/bancos', 'editar')
    },
    puedeEliminar() {
      return this.$store.getters.puedoHacer('/finanzas/bancos', 'eliminar')
    },
  },

  async created() {
    await this.cargar()
  },

  methods: {
    async cargar(pagina = 1) {
      this.cargando = true
      this.error    = null
      try {
        const id = this.$route.params.id
        const { data } = await api.get(`/bancos/${id}/movimientos?pagina=${pagina}`)
        this.banco       = data.banco
        this.saldoActual = data.saldo_actual
        this.resumen     = data.resumen
        this.movimientos = data.movimientos
        this.paginacion  = data.paginacion
      } catch {
        this.error = 'Error al cargar los movimientos'
      } finally {
        this.cargando = false
      }
    },

    cambiarPagina(p) {
      this.cargar(p)
    },

    esManual(m) {
      return ['transferencia', 'ajuste'].includes(m.origen)
    },

    abrirEditar(m) {
      this.modalEditar = {
        visible: true, guardando: false, error: null,
        id: m.id,
        monto: parseFloat(m.monto || 0),
        descripcion: m.descripcion || '',
        esTransferencia: m.origen === 'transferencia',
      }
    },

    async guardarEdicion() {
      this.modalEditar.guardando = true
      this.modalEditar.error     = null
      try {
        await api.put(`/bancos/movimientos/${this.modalEditar.id}`, {
          monto:       this.modalEditar.monto,
          descripcion: this.modalEditar.descripcion,
        })
        this.modalEditar.visible = false
        await this.cargar(this.paginacion.pagina)
      } catch (e) {
        this.modalEditar.error = e?.response?.data?.error || 'Error al guardar'
      } finally {
        this.modalEditar.guardando = false
      }
    },

    async eliminar(m) {
      const msg = m.origen === 'transferencia'
        ? `¿Eliminar el depósito de $${this.fmt(m.monto)}? También se eliminará el movimiento de caja asociado.`
        : `¿Eliminar el movimiento de $${this.fmt(m.monto)}?`
      if (!confirm(msg)) return
      try {
        await api.delete(`/bancos/movimientos/${m.id}`)
        await this.cargar(this.paginacion.pagina)
      } catch (e) {
        alert(e?.response?.data?.error || 'Error al eliminar')
      }
    },

    fmt(val) { return parseFloat(val || 0).toFixed(2) },

    formatearFechaHora(fecha) {
      return formatearFechaHoraSistema(fecha)
    },

    abrirExterno() {
      this.modalExterno = {
        visible: true, guardando: false, error: null,
        tipo: 'egreso', monto: 0, descripcion: '', numero_comprobante: '',
      }
    },

    async guardarExterno() {
      this.modalExterno.error = null
      if (!this.modalExterno.monto || parseFloat(this.modalExterno.monto) <= 0) {
        this.modalExterno.error = 'El monto debe ser mayor a cero'
        return
      }
      if (!this.modalExterno.descripcion.trim()) {
        this.modalExterno.error = 'La descripción es obligatoria'
        return
      }
      this.modalExterno.guardando = true
      try {
        const id = this.$route.params.id
        await api.post(`/bancos/${id}/movimientos`, {
          tipo:               this.modalExterno.tipo,
          monto:              this.modalExterno.monto,
          descripcion:        this.modalExterno.descripcion.trim(),
          numero_comprobante: this.modalExterno.numero_comprobante.trim() || null,
        })
        this.modalExterno.visible = false
        await this.cargar(this.paginacion.pagina)
      } catch (e) {
        this.modalExterno.error = e?.response?.data?.error || 'Error al registrar el movimiento'
      } finally {
        this.modalExterno.guardando = false
      }
    },
  },
}
</script>

<style scoped>
/* Tarjetas de resumen — variables del sistema para claro/oscuro */
.resumen-card {
  background: var(--secondary-bg-main);
  color: var(--text-primary);
}
.resumen-card-saldo {
  background: var(--bg-main);
  border: 1px solid var(--border-main) !important;
}
.resumen-label   { color: var(--text-muted); }
.resumen-valor   { color: var(--text-secondary); }
.resumen-ingreso { color: var(--accent-success); }
.resumen-egreso  { color: var(--accent-danger, #e85454); }
.resumen-saldo   { color: var(--primary-main); }
</style>
